import { useEffect, useState } from "react";
import Select from "react-select";
import * as actions from '../../../redux/actions/growthEngine';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import Error from '../../../helper/Error'
import { isAdAccountCampaignReady } from '../../../helper/utility'
import MusicLoader from '../../../helper/MusicLoader';
import { BiRefresh } from "react-icons/bi";
// Custom Styles for the Dropdown
const customStyles = {
    control: (base) => ({
        ...base,
        backgroundColor: "#fa6138",
        borderRadius: "12px",
        border: "2px solid #00067e",
        "&:hover": {
            borderColor: "#00067e", // Border color on hover
        },
        "&:focus": {
            outline: "none",
            boxShadow: "none"
        },
        padding: "5px",
        minHeight: "40px",
    }),
    menu: (base) => ({
        ...base,
        borderRadius: "12px",
        backgroundColor: "white",
        border: "2px solid #00067e",
        color: "#00067e",
        padding: "0",
        outline: "none",
        overflow: "hidden"
    }),
    option: (base) => ({
        ...base,
        backgroundColor: "#fff",
        padding: "5px 16px",
        color: "#00067e",
        fontSize: "12px",
        fontWeight: "500",
    }),
    singleValue: (base) => ({
        ...base,
        color: "#00067e",
        fontSize: "12px",
        fontWeight: "500",
    }),
    placeholder: (base) => ({
        ...base,
        color: "#00067e",
        fontSize: "12px",
        fontWeight: "500",
    }),
};
const formatNumber = (num) => {
    if (num === 0) return "0";
    if (num < 1000) return num.toString();
    if (num >= 1_000 && num < 1_000_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + "K";
    if (num >= 1_000_000 && num < 1_000_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + "M";
    if (num >= 1_000_000_000 && num < 1_000_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + "B";
    if (num >= 1_000_000_000_000) return (num / 1_000_000_000_000).toFixed(1).replace(/\.0$/, '') + "T";
    return num.toString();
};

export const StepTwo = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { formData } = useSelector(state => state.growthEngine);
    const { connectedChannels, selectedChannel } = formData
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [connectLoading, setConnectLoading] = useState(false);
    const [removingChannelId, setRemovingChannelId] = useState(null);
    const [connectChanel, setConnectChanel] = useState(null);
    const [loadingAdAccounts, setLoading] = useState(false)
    const [disconnectingMeta, setDisconnectingMeta] = useState(false)
    const [connectingMeta, setConnectingMeta] = useState(false)
    // Cover first paint after Meta redirect (URL still has ?code= before exchange finishes)
    const [oauthCodeInUrl, setOauthCodeInUrl] = useState(() =>
        Boolean(new URLSearchParams(window.location.search).get("code"))
    );
    const isMetaAuthInProgress = connectingMeta || formData.metaAuthPending || oauthCodeInUrl;

    useEffect(() => {
        // After exchange finishes (success or fail), drop URL-based pending flag
        if (!formData.metaAuthPending && oauthCodeInUrl) {
            const stillHasCode = Boolean(new URLSearchParams(window.location.search).get("code"));
            if (!stillHasCode) setOauthCodeInUrl(false);
        }
        if (formData.metaLoggedIn || formData.facebookUserId) {
            setOauthCodeInUrl(false);
        }
    }, [formData.metaAuthPending, formData.metaLoggedIn, formData.facebookUserId, oauthCodeInUrl]);

    const getFilteredPlatformOptions = (objective) => {
        if (objective === "Instagram Growth" || objective === "Promote Custom Link" || objective === "Promote an Event" || objective === "Sell Your Merch"
            || objective === "Promote Bio Link" || objective === "YouTube Growth"
        ) {
            return [
                { id: "Facebook", label: "Facebook", icon: "bi-facebook" },
                { id: "Instagram", label: "Instagram", icon: "bi-instagram" }
            ];
        }
        if (objective === "Playlist Growth" || objective === "Spotify Growth" || objective === "More Streams") {
            return [
                { id: "Facebook", label: "Facebook", icon: "bi-facebook" },
                { id: "Instagram", label: "Instagram", icon: "bi-instagram" },
                // { id: "TikTok", label: "TikTok", icon: "bi-tiktok" }
            ];
        }

        return [
            { id: "Facebook", label: "Facebook", icon: "bi-facebook" },
            { id: "Instagram", label: "Instagram", icon: "bi-instagram" },
            // { id: "YouTube", label: "YouTube", icon: "bi-youtube" },
            // { id: "TikTok", label: "TikTok", icon: "bi-tiktok" }
        ];
    };

    const platformOptions = getFilteredPlatformOptions(formData?.objective);
    const isInstagramGrowth = formData.objective === "Instagram Growth";

    useEffect(() => {
        if (formData.objective === "YouTube Growth" && formData.platformType === "YouTube") {
            dispatch(actions.updateFormData({
                platformType: "",
                selectedChannel: "",
            }));
        }
    }, [formData.objective, formData.platformType, dispatch]);

    const getInstagramOptionsForPage = (pageId) =>
        (formData.pageList || [])
            .filter((page) => page.id === pageId && page.instagram_id)
            .map((page) => ({
                value: page.instagram_id,
                label: page.instagram_name,
                image: page.instagram_profile_picture,
                instagram_profile_url: page.instagram_profile_url,
                pageId: page.id,
            }));

    const instagramOptions = getInstagramOptionsForPage(formData.pageId);
    const selectedMetaPage = (formData.pageList || []).find((page) => page.id === formData.pageId) || null;
    const linkedInstagramId = selectedMetaPage?.instagram_id ? String(selectedMetaPage.instagram_id) : "";
    const storedInstagramId = formData.instagramId ? String(formData.instagramId) : "";
    const instagramSelectionMatchesPage = Boolean(storedInstagramId && linkedInstagramId && storedInstagramId === linkedInstagramId);

    const getInstagramProfileUrl = (pageOrOption) => {
        if (!pageOrOption) return "";
        if (pageOrOption.instagram_profile_url) return pageOrOption.instagram_profile_url;
        const username = pageOrOption.instagram_name || pageOrOption.label;
        return username ? `https://www.instagram.com/${username}` : "";
    };

    const selectedInstagramOption = isInstagramGrowth
        ? (instagramSelectionMatchesPage
            ? instagramOptions.find((option) => String(option.value) === storedInstagramId) || null
            : null)
        : instagramOptions[0] || null;

    // Function to handle platform selection
    const handlePlatformSelect = (selectedPlatform) => {
        let newPlatformType = selectedPlatform;
        // If Facebook is selected, set it to Instagram, and vice versa
        if (selectedPlatform === "Facebook" || selectedPlatform === "Instagram") {
            if (formData.platformType === "Facebook" || formData.platformType === "Instagram") {
                newPlatformType = ""; // Unselect both if one is already selected
            } else {
                newPlatformType = selectedPlatform;
            }
        } else if (selectedPlatform === "YouTube") {
            if (connectedChannels?.length <= 0) {
                dispatch(actions.getConnectYoutubeChannel(toast, setConnectLoading));
            }
        }
        dispatch(actions.updateFormData({ platformType: newPlatformType }));
    };

    const handleMetaLogin = async () => {
        if (isMetaAuthInProgress || formData.metaLoggedIn || formData.facebookUserId) return;
        await dispatch(actions.generateFacebookUrl(toast, setConnectingMeta));
    };

    const handleMetaLogout = async () => {
        setDisconnectingMeta(true);
        try {
            await dispatch(actions.disconnectMetaAccount(toast, queryClient));
        } finally {
            setDisconnectingMeta(false);
        }
    };

    // meta page selection
    const handlePageChange = (selectedOption) => {
        const updates = {
            pageId: selectedOption.value,
            pageAccessToken: '',
        };

        if (isInstagramGrowth) {
            updates.instagramId = '';
            updates.destinationUrl = '';
        } else {
            updates.instagramId = selectedOption?.instagram_id || '';
        }

        dispatch(actions.updateFormData(updates));
    };

    const handleInstagramChange = (selectedOption) => {
        dispatch(actions.updateFormData({
            instagramId: selectedOption?.value || '',
            destinationUrl: getInstagramProfileUrl(selectedOption),
        }));
    };

    useEffect(() => {
        if (!isInstagramGrowth) return;

        if (formData.instagramId && selectedMetaPage && !instagramSelectionMatchesPage) {
            dispatch(actions.updateFormData({ instagramId: '', destinationUrl: '' }));
            return;
        }

        if (instagramSelectionMatchesPage) {
            const profileUrl = getInstagramProfileUrl(selectedMetaPage);
            if (profileUrl && formData.destinationUrl !== profileUrl) {
                dispatch(actions.updateFormData({ destinationUrl: profileUrl }));
            }
        }
    }, [
        isInstagramGrowth,
        formData.instagramId,
        formData.destinationUrl,
        formData.pageId,
        instagramSelectionMatchesPage,
        selectedMetaPage,
        dispatch,
    ]);

    useEffect(() => {
        if (!formData.adAccountId && formData.adAccountList?.length > 0) {
            dispatch(actions.updateFormData({ adAccountId: formData.adAccountList[0].id }));
        }
    }, [formData.adAccountId, formData.adAccountList, dispatch]);

    useEffect(() => {
        if (!formData.adAccountId) return;
        const selectedAccount = formData.adAccountList?.find((a) => a.id === formData.adAccountId);
        if (selectedAccount?.currency && selectedAccount.currency !== formData.currency) {
            dispatch(actions.updateFormData({ currency: selectedAccount.currency }));
        }
        dispatch(actions.fetchAdAccountBudgetLimits(formData.adAccountId, toast));
    }, [formData.adAccountId, formData.adAccountList?.length, dispatch]);

    // useEffect(() => {
    //     if (connectedChannels?.length <= 0 && formData.platformType === 'YouTube') {
    //         const fetchChannel = async () => {
    //             await dispatch(actions.getConnectYoutubeChannel(toast, setConnectLoading));
    //         }
    //         fetchChannel()
    //     }
    // }, [connectedChannels, formData.platformType, dispatch]);


    // youtube related functionality
    const handleSearchChannel = async () => {
        if (!searchQuery.trim()) return;
        const results = await dispatch(actions.searchYoutubeChannel(searchQuery, toast, setSearchLoading));
        if (results) {
            setSearchResults(results);
            setSearchQuery('');
        }
    };


    const handleConnectChannel = async (channel) => {
        if (!connectedChannels.some((ch) => ch.channelId === channel.channelId)) {
            let data = {
                channelId: channel?.channelId,
                title: channel?.title,
                customUrl: channel?.customUrl,
                thumbnail: channel?.thumbnail,
                handle: channel?.handle
            }
            await dispatch(actions.connectYoutubeChannel(data, toast, setConnectLoading));
        }
    };

    const handleSelectConnectedChannel = (channelId) => {
        dispatch(actions.updateFormData({ selectedChannel: channelId }));
    };

    const handleRemoveChannel = async (channelId) => {
        setRemovingChannelId(channelId);
        await dispatch(actions.removeYoutubeChannel(channelId, toast, setRemovingChannelId));
    };

    const handleRefreshAdAccounts = async () => {
        try {
            setLoading(true)
            if (formData?.metaLoggedIn || formData?.facebookUserId) {
                await dispatch(actions.getAdAccount(toast))
            }
        } catch (error) {
            setLoading(false)
        } finally {
            setLoading(false)
        }
    }


    return (
        <div className="font-inter text-theme_clr1 mb-8 xl:mb-[61px]" data-tour="select-platform">
            <div className="heading text-white font-medium text-center mb-[28.5px]">
                <h5 className="text-[22.5px] mb-1">Choose your platform</h5>
                <div className="description text-white text-opacity-80 text-[15px]">Decide which platform you want your ads on, and which account to post from</div>
            </div>
            <div className="info-box bg-white rounded-xl p-4 lg:p-5">
                <div className="flex flex-col lg:flex-row gap-4 xl:gap-5">
                    <div className="left-info lg:w-[40%]">
                        <div className="title-name text-[22.69px] font-medium mb-[9px]">Choose your platform</div>
                        <div className="description font-medium text-theme_clr1 text-opacity-55 text-[15px]">
                            Choose the social media platform you’d like to advertise on (if you’re unsure select all available).
                        </div>
                    </div>
                    <div className="right-info lg:w-[60%]">
                        <div className="flex flex-col gap-2">
                            {platformOptions.map(({ id, label, icon }) => (
                                <div key={id} className="item relative">
                                    <input
                                        type="checkbox"
                                        className="peer hidden"
                                        id={id}
                                        autoComplete="off"
                                        checked={formData.platformType === id || (formData.platformType === "Facebook" && id === "Instagram") || (formData.platformType === "Instagram" && id === "Facebook")}
                                        onChange={() => handlePlatformSelect(id)}
                                    />
                                    <label
                                        className="font-medium flex items-center gap-5 bg-transparent peer-checked:bg-theme_clr2 border-2 border-theme_clr1 rounded-2xl p-4"
                                        htmlFor={id}
                                    >
                                        <i className={`bi ${icon} text-2xl leading-none`}></i> {label}
                                    </label>
                                    {/* Icon to be shown when checkbox is checked */}
                                    <div className="icon absolute top-1/2 -translate-y-1/2 right-4 hidden peer-checked:block">
                                        <i className="bi bi-check-circle-fill text-theme_clr1 text-xl"></i>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <hr className="border-black my-4" />

                <div className="flex flex-col lg:flex-row gap-4 xl:gap-5">
                    <div className="left-info lg:w-[40%]">
                        <div className="title-name text-[22.69px] font-medium mb-2">Choose your account</div>
                        <div className="description font-medium text-theme_clr1 text-opacity-55 text-[15px]">
                            Choose the advertising account you want this campaign created under.
                        </div>
                        {formData.platformType === "YouTube" && (
                            < div className="w-[280px] h-fit bg-yellow-50 border border-yellow-300 rounded-xl p-4 flex flex-col items-start shadow-sm mt-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <i className="bi bi-exclamation-triangle-fill text-yellow-600"></i>
                                    <span className="font-semibold text-sm text-yellow-700">Test Mode</span>
                                </div>
                                <p className="text-xs text-yellow-700">
                                    This module is currently in <strong>Test Mode</strong>.
                                    The <strong>Pro version</strong> will be available soon.
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="right-info lg:w-[60%]">
                        <div className="btn-wrapper flex gap-4 xl:gap-[41px] mb-3">
                            {(formData.platformType === "Facebook" || formData.platformType === "Instagram") && !(formData.metaLoggedIn || formData.facebookUserId) ? (
                                <button
                                    type="button"
                                    disabled={isMetaAuthInProgress}
                                    className="text-xs font-medium text-theme_clr1 bg-transparent hover:bg-theme_clr1 hover:text-white focus:bg-theme_clr1 focus:text-white border-2 border-theme_clr1 rounded-lg transition-all px-4 py-[11px] disabled:opacity-60 disabled:cursor-wait flex items-center gap-2"
                                    onClick={handleMetaLogin}
                                >
                                    {isMetaAuthInProgress ? (
                                        <>
                                            <i className="bi bi-music-note-beamed animate-pulse text-theme_clr1 text-sm" />
                                            Connecting…
                                        </>
                                    ) : (
                                        'Login to Meta'
                                    )}
                                </button>
                            ) : null}
                            {(formData.platformType === "Facebook" || formData.platformType === "Instagram") && (formData.metaLoggedIn || formData.facebookUserId) ? (
                                <button
                                    type="button"
                                    disabled={disconnectingMeta}
                                    className="text-xs font-medium text-theme_clr1 bg-transparent hover:bg-theme_clr1 hover:text-white focus:bg-theme_clr1 focus:text-white border-2 border-theme_clr1 rounded-lg transition-all px-4 py-[11px] disabled:opacity-60"
                                    onClick={handleMetaLogout}
                                >
                                    {disconnectingMeta ? "Logging out…" : "Logout from Meta"}
                                </button>
                            ) : null}
                            {/* {formData.platformType === "TikTok" ? (
                                <button className="text-xs font-medium text-theme_clr1 bg-transparent hover:bg-theme_clr1 hover:text-white focus:bg-theme_clr1 focus:text-white border-2 border-theme_clr1 rounded-lg transition-all px-4 py-[11px]">Login to TikTok</button>
                            ) : null} */}
                            {formData.platformType === "TikTok" ? (
                                <button className="text-xs font-medium text-theme_clr1 bg-transparent hover:bg-theme_clr1 hover:text-white focus:bg-theme_clr1 focus:text-white border-2 border-theme_clr1 rounded-lg transition-all px-4 py-[11px]">Coming soon...</button>
                            ) : null}
                            {/* {formData.platformType === "YouTube" && formData.isGoogleLogin === false ? (
                                <button className="text-xs font-medium text-theme_clr1 bg-transparent hover:bg-theme_clr1 hover:text-white focus:bg-theme_clr1 focus:text-white border-2 border-theme_clr1 rounded-lg transition-all px-4 py-[11px]" onClick={handleGoogleLogin} >Login to Google</button>
                            ) : null} */}
                        </div>

                        <div className="account-info-wrapper flex flex-col gap-3">
                            {/* start meta account function */}
                            <>
                                {/* meta advetisement account  */}
                                {(formData.metaLoggedIn || formData.facebookUserId) && (formData.platformType === "Facebook" || formData.platformType === "Instagram") && (
                                    // <div className="bg-white border-2 border-theme_clr1 rounded-2xl p-4">
                                    //     <div className="heading mb-5">
                                    //         <div className="title text-sm font-medium mb-1">Select a Facebook advertising account</div>
                                    //         <div className="desc text-xs font-medium">Select a Facebook account you want this campaign created under</div>
                                    //     </div>
                                    //     {
                                    //         formData?.adAccountList?.length > 0 ? (
                                    //             <div className="select-wrapper text-theme_clr1 text-xs font-medium bg-theme_clr2 border-2 border-theme_clr1 rounded-xl p-3">
                                    //                 {(() => {
                                    //                     const selectedAccount = formData.adAccountList.find(account => account.id === formData.adAccountId) || formData.adAccountList[0];
                                    //                     const { isReady, notReadyReasons } = isAdAccountCampaignReady(selectedAccount);
                                    //                     return (
                                    //                         <>
                                    //                             <label htmlFor="accountID" className="block text-theme_clr1 text-xs font-medium mb-1 ps-1">
                                    //                                 <span className="text-theme_clr1">{selectedAccount.id}</span>
                                    //                                 <span className={`ml-2 font-semibold ${selectedAccount.account_status === 1 ? "text-[#117205]" : "text-red-500"}`}>
                                    //                                     ({selectedAccount.account_status === 1 ? "Active" : "Inactive"})
                                    //                                 </span>
                                    //                             </label>

                                    //                             {!isReady && (
                                    //                                 <ul className="text-white-500 text-[11px] mt-1 list-disc ps-4 space-y-[2px]">
                                    //                                     {notReadyReasons.map((reason, idx) => (
                                    //                                         <li key={idx}>{reason}</li>
                                    //                                     ))}
                                    //                                 </ul>
                                    //                             )}
                                    //                         </>
                                    //                     );
                                    //                 })()}

                                    //                 <select
                                    //                     id="accountID"
                                    //                     className="w-full focus:outline-none rounded-md p-2"
                                    //                     value={formData.adAccountId || formData.adAccountList[0]?.id}
                                    //                     onChange={(e) => {
                                    //                         const selectedAccount = formData.adAccountList.find(account => account.id === e.target.value);
                                    //                         if (selectedAccount) {
                                    //                             const { isReady } = isAdAccountCampaignReady(selectedAccount || {});
                                    //                             if (!isReady) {
                                    //                                 toast.warning("Please select a valid ad account", {
                                    //                                     position: "top-center",
                                    //                                     theme: "colored",
                                    //                                     hideProgressBar: true,
                                    //                                 });
                                    //                             }
                                    //                             dispatch(actions.updateFormData({ adAccountId: selectedAccount.id }));
                                    //                         }
                                    //                     }}
                                    //                 >
                                    //                     {formData.adAccountList.map((account, index) => (
                                    //                         <option key={index} value={account.id}>
                                    //                             {account.name} (ID: {account.id}) {account.account_status === 1 ? "Active" : "Inactive"}
                                    //                         </option>
                                    //                     ))}
                                    //                 </select>
                                    //             </div>
                                    //         ) : (
                                    //             <Error errorMessage="No Account Id found" />
                                    //         )
                                    //     }
                                    // </div>
                                    <div className="bg-white border-2 border-theme_clr1 rounded-2xl p-4 relative">

                                        {/* Top-right Refresh Button */}
                                        <button
                                            onClick={handleRefreshAdAccounts}
                                            disabled={loadingAdAccounts}
                                            className="absolute top-3 right-3 p-2 rounded-lg border border-theme_clr1 text-theme_clr1
                                            hover:bg-theme_clr1 hover:text-white transition flex items-center justify-center"
                                        >
                                            <BiRefresh
                                                size={16}
                                                className={loadingAdAccounts ? "animate-spin" : ""}
                                            />
                                        </button>
                                        <div className="heading mb-5">
                                            <div className="title text-sm font-medium mb-1">
                                                Select a Facebook advertising account
                                            </div>
                                            <div className="desc text-xs font-medium">
                                                Select a Facebook account you want this campaign created under
                                            </div>
                                        </div>

                                        {/* Skeleton while loading */}
                                        {loadingAdAccounts ? (
                                            <div className="animate-pulse space-y-3">
                                                <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                                                <div className="h-10 bg-gray-200 rounded"></div>
                                            </div>
                                        ) : (
                                            <>
                                                {formData?.adAccountList?.length > 0 ? (
                                                    <div className="select-wrapper text-theme_clr1 text-xs font-medium bg-theme_clr2 border-2 border-theme_clr1 rounded-xl p-3">

                                                        {(() => {
                                                            const selectedAccount =
                                                                formData.adAccountList.find(a => a.id === formData.adAccountId) ||
                                                                formData.adAccountList[0];

                                                            const { isReady, notReadyReasons } =
                                                                isAdAccountCampaignReady(selectedAccount);

                                                            return (
                                                                <>
                                                                    <label
                                                                        htmlFor="accountID"
                                                                        className="block text-theme_clr1 text-xs font-medium mb-1 ps-1"
                                                                    >
                                                                        <span className="text-theme_clr1">{selectedAccount.id}</span>
                                                                        <span
                                                                            className={`ml-2 font-semibold ${selectedAccount.account_status === 1
                                                                                ? "text-[#117205]"
                                                                                : "text-red-500"
                                                                                }`}
                                                                        >
                                                                            ({selectedAccount.account_status === 1
                                                                                ? "Active"
                                                                                : "Inactive"})
                                                                        </span>
                                                                    </label>

                                                                    {!isReady && (
                                                                        <ul className="text-white-500 text-[11px] mt-1 list-disc ps-4 space-y-[2px]">
                                                                            {notReadyReasons.map((r, idx) => (
                                                                                <li key={idx}>{r}</li>
                                                                            ))}
                                                                        </ul>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}

                                                        <select
                                                            id="accountID"
                                                            className="w-full focus:outline-none rounded-md p-2"
                                                            value={formData.adAccountId || formData.adAccountList[0]?.id}
                                                            onChange={(e) => {
                                                                const selected = formData.adAccountList.find(
                                                                    account => account.id === e.target.value
                                                                );

                                                                if (selected) {
                                                                    const { isReady } = isAdAccountCampaignReady(selected);

                                                                    if (!isReady) {
                                                                        toast.warning("Please select a valid ad account", {
                                                                            position: "top-center",
                                                                            theme: "colored",
                                                                            hideProgressBar: true,
                                                                        });
                                                                    }

                                                                    dispatch(actions.updateFormData({
                                                                        adAccountId: selected.id,
                                                                        currency: selected.currency || '',
                                                                    }));
                                                                    dispatch(actions.fetchAdAccountBudgetLimits(selected.id, toast));
                                                                }
                                                            }}
                                                        >
                                                            {formData.adAccountList.map((account, index) => (
                                                                <option key={index} value={account.id}>
                                                                    {account.name} (ID: {account.id}){" "}
                                                                    {account.account_status === 1 ? "Active" : "Inactive"}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ) : (
                                                    <Error errorMessage="No Account Id found" />
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                                {/* Meta Account Selection */}
                                {(formData.metaLoggedIn || formData.facebookUserId) && (formData.platformType === "Facebook" || formData.platformType === "Instagram") && (
                                    <div className="bg-white border-2 border-theme_clr1 rounded-2xl p-4">
                                        <div className="heading mb-5">
                                            <div className="title text-sm font-medium mb-1">Select a Facebook page</div>
                                            <div className="desc text-xs font-medium">Choose a Facebook page you want your ad to be posted by</div>
                                        </div>
                                        {
                                            formData?.pageList?.length > 0 ? (
                                                <div className="select-wrapper">
                                                    <div className="input-cover">
                                                        <Select
                                                            className="custom-select font-inter"
                                                            onChange={handlePageChange}
                                                            options={formData.pageList.map(page => ({
                                                                value: page.id, // Setting the ID as the value
                                                                label: page.name, // Display name
                                                                image: page.profile_picture, // Profile picture URL
                                                                instagram_id: page.instagram_id,
                                                                instagram_profile_url: page.instagram_profile_url

                                                            }))}
                                                            styles={customStyles}
                                                            getOptionLabel={(info) => (
                                                                <div className="flex items-center">
                                                                    <img
                                                                        src={info.image}
                                                                        alt={info.label}
                                                                        className="w-[32px] h-[32px] rounded-full mr-2"
                                                                    />
                                                                    <span className="text-xs font-medium">
                                                                        {info.label}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            value={formData.pageList.find(page => page.id === formData.pageId)
                                                                ? {
                                                                    value: formData.pageId,
                                                                    label: formData.pageList.find(page => page.id === formData.pageId)?.name,
                                                                    image: formData.pageList.find(page => page.id === formData.pageId)?.profile_picture,
                                                                    instagram_id: formData.pageList.find(page => page.id === formData.pageId)?.instagram_id,
                                                                    instagram_profile_url: formData.pageList.find(page => page.id === formData.pageId)?.instagram_profile_url,

                                                                }
                                                                : null}
                                                            isSearchable={true}
                                                            placeholder="Select Facebook Page"
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <Error errorMessage="No Page found for this account" />
                                            )
                                        }


                                    </div>
                                )}
                                {(formData.metaLoggedIn || formData.facebookUserId) && (formData.platformType === "Instagram" || formData.platformType === "Facebook") && (
                                    <div className="bg-white border-2 border-theme_clr1 rounded-2xl p-4">
                                        <div className="heading mb-5">
                                            <div className="title text-sm font-medium mb-1">
                                                Select an Instagram page{isInstagramGrowth ? " *" : ""}
                                            </div>
                                            <div className="desc text-xs font-medium">
                                                {isInstagramGrowth
                                                    ? "Choose the Instagram account you want to grow — required for this campaign"
                                                    : "Choose an Instagram page you want your ad to be posted by"}
                                            </div>
                                        </div>
                                        {!formData.pageId ? (
                                            <div className="text-theme_clr1 text-opacity-55 text-xs font-medium">
                                                Select a Facebook page first
                                            </div>
                                        ) : instagramOptions.length === 0 ? (
                                            <Error errorMessage="No Instagram account linked to this Facebook page" />
                                        ) : (
                                        <div className="select-wrapper">
                                            <div className="input-cover">
                                                <Select
                                                    className="custom-select font-inter"
                                                    onChange={isInstagramGrowth ? handleInstagramChange : undefined}
                                                    isDisabled={!isInstagramGrowth}
                                                    options={instagramOptions}
                                                    styles={customStyles}
                                                    getOptionLabel={(info) => (
                                                        <div className="flex items-center">
                                                            <img src={info.image} alt={info.label} className="w-[32px] h-[32px] rounded-full mr-2" />
                                                            <span className="text-xs font-medium">{info.label}</span>
                                                        </div>
                                                    )}
                                                    value={selectedInstagramOption}
                                                    isSearchable={true}
                                                    placeholder="Select Instagram Page"
                                                    isClearable={isInstagramGrowth}
                                                />
                                            </div>
                                        </div>
                                        )}
                                    </div>
                                )}
                            </>
                            {/* end meta account function */}

                            {/* start google account function  */}
                            {formData.platformType === "YouTube" && (
                                <div className="bg-white border-2 border-theme_clr1 rounded-2xl p-4">
                                    <div className="heading mb-5">
                                        <div className="title text-sm font-medium mb-1">Select a YouTube Channel</div>
                                        <div className="desc text-xs font-medium">
                                            Search and connect your YouTube channel to create a campaign
                                        </div>
                                    </div>

                                    {/* Search Input */}
                                    <div className="mb-4 flex items-center gap-3">
                                        {/* YouTube Icon */}
                                        <i
                                            className="bi bi-youtube text-red-600 text-3xl flex-shrink-0"
                                            style={{ height: "32px", width: "32px", lineHeight: "32px" }}
                                        ></i>

                                        {/* Search Input */}
                                        <div className="relative flex-1">
                                            <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm"></i>
                                            <input
                                                type="text"
                                                placeholder="Search YouTube channel by name or URL"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="w-full border border-theme_clr1 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-theme_clr1"
                                            />
                                        </div>

                                        {searchLoading ? (
                                            <div className="flex items-center justify-center h-[36px] w-[90px]">
                                                <MusicLoader />
                                            </div>
                                        ) : (
                                            <button
                                                onClick={handleSearchChannel}
                                                className="bg-theme_clr1 text-white rounded-lg px-4 py-2 text-sm hover:opacity-90 whitespace-nowrap"
                                            >
                                                Search
                                            </button>
                                        )}
                                    </div>

                                    {/* Search Results */}
                                    {searchResults.length > 0 && (
                                        <div className="mb-4 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                                            {searchResults.map((channel) => {
                                                const isConnected = connectedChannels?.some(
                                                    (c) => c.channelId === channel.channelId
                                                );

                                                return (
                                                    <div
                                                        key={channel.channelId}
                                                        onClick={() => setConnectChanel(channel)}
                                                        className={`flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50 ${connectChanel?.channelId === channel.channelId ? "bg-gray-100" : ""
                                                            }`}
                                                    >
                                                        {/* Channel Info */}
                                                        <div className="flex items-center gap-3">
                                                            <img
                                                                src={channel.thumbnail}
                                                                alt={channel.title}
                                                                className="w-10 h-10 rounded-full"
                                                            />
                                                            <div>
                                                                <div className="font-medium text-sm">{channel?.title}</div>
                                                                <div className="text-xs text-gray-500">{channel?.handle}</div>
                                                                <div className="text-xs text-gray-500">
                                                                    {channel?.subscriberCount
                                                                        ? `${formatNumber(channel?.subscriberCount)} Subscribers`
                                                                        : "Subscriber count hidden"}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Connect / Connected Button */}
                                                        <button
                                                            disabled={isConnected || connectLoading}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (!isConnected && !connectLoading) handleConnectChannel(channel);
                                                            }}
                                                            className={`text-xs font-medium rounded-md px-3 py-1 border flex items-center justify-center transition ${isConnected
                                                                ? "border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed"
                                                                : connectLoading
                                                                    ? "border-theme_clr1 text-theme_clr1 bg-gray-50 cursor-wait"
                                                                    : "border-theme_clr1 text-theme_clr1 hover:bg-theme_clr1 hover:text-white"
                                                                }`}
                                                        >
                                                            {connectLoading ? (
                                                                // Music loader here (use whatever loader component or icon you have)
                                                                <div className="flex items-center justify-center gap-1">
                                                                    <i className="bi bi-music-note-beamed animate-pulse text-theme_clr1 text-sm"></i>
                                                                    <span className="text-xs">Connecting...</span>
                                                                </div>
                                                            ) : isConnected ? (
                                                                "Connected ✓"
                                                            ) : (
                                                                "Connect"
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Connected Channels Dropdown */}
                                    <div className="mt-5">
                                        <label className="block text-xs font-medium mb-2 ps-1">
                                            Connected YouTube Channels
                                        </label>

                                        {connectedChannels.length > 0 ? (
                                            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                                                {connectedChannels.map((ch) => {
                                                    const isSelected = selectedChannel === ch.channelId;
                                                    return (
                                                        <div
                                                            key={ch.channelId}
                                                            onClick={() => handleSelectConnectedChannel(ch.channelId)}
                                                            className={`flex items-center justify-between p-3 cursor-pointer transition ${isSelected ? "bg-orange-100 border-l-4 border-orange-400" : "hover:bg-gray-50"
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <img
                                                                    src={ch.thumbnail}
                                                                    alt={ch.title}
                                                                    className="w-10 h-10 rounded-full"
                                                                />
                                                                <div>
                                                                    <div className="font-medium text-sm">{ch.title}</div>
                                                                    <div className="text-xs text-gray-500">{ch?.handle}</div>
                                                                    {/* <div className="text-xs text-gray-500">
                                                                        {ch.subscriberCount
                                                                            ? `${ch.subscriberCount.toLocaleString()} Subscribers`
                                                                            : "Subscriber count hidden"}
                                                                    </div> */}
                                                                </div>
                                                            </div>

                                                            <button
                                                                disabled={removingChannelId === ch.channelId}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (removingChannelId !== ch.channelId) handleRemoveChannel(ch.channelId);
                                                                }}
                                                                className={`text-xs font-medium rounded-md px-3 py-1 border transition flex items-center justify-center ${removingChannelId === ch.channelId
                                                                    ? "border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed"
                                                                    : "text-red-500 border border-red-400 hover:bg-red-500 hover:text-white"
                                                                    }`}
                                                                style={{ minWidth: "80px", height: "30px" }}
                                                            >
                                                                {removingChannelId === ch.channelId ? <MusicLoader size={16} /> : "Remove"}
                                                            </button>


                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-gray-500 ps-1">No channels connected yet</div>
                                        )}
                                    </div>

                                </div>

                            )}
                            {/* end google account function  */}
                        </div>
                    </div>
                </div>
            </div>
        </div >
    )
}
